/**
 * Task → record mapping (v1 of the task↔record link, plan item 13).
 *
 * Maps a completed task to the livestock record type it implies, so the
 * frontend can offer an opt-in "register the record?" prompt after
 * completion. Resolution order: exact `task.operationType` match first,
 * pt-BR title-keyword fallback second. Lives in shared-types so a future
 * backend consumer (e.g. suggested records) uses the same table.
 *
 * Provenance convention (documented, not enforced): records created from a
 * task set `metadata.sourceTaskId = task.id` on operations; measurements
 * have no metadata column, so v1 appends a `[task:<id>]` suffix to `notes`
 * (accepted as lossy). One-way only — tasks never reference records in v1.
 */
import { LivestockMeasurementType, LivestockOperationType } from "./Common.js";
const operation = (type) => ({
    kind: "operation",
    type,
});
const measurement = (type) => ({
    kind: "measurement",
    type,
});
/**
 * task.operationType → record type. Exact-match, additive. Keys are the
 * livestock enum values a livestock-created task carries in its free-text
 * `operationType` column. SALE/OTHER are deliberately unmapped (sales go
 * through the sale flow; OTHER is not a useful prompt). Legacy measurement
 * types PREGNANCY_CHECK/CALVING are unmapped — the reproduction operation
 * types are the canonical write path.
 */
export const TASK_OPERATION_TYPE_TO_RECORD = {
    VACCINATION: operation(LivestockOperationType.VACCINATION),
    TREATMENT: operation(LivestockOperationType.TREATMENT),
    DISEASE: operation(LivestockOperationType.DISEASE),
    FEEDING_PLAN: operation(LivestockOperationType.FEEDING_PLAN),
    SUPPLEMENTATION: operation(LivestockOperationType.SUPPLEMENTATION),
    GRAZING_ROTATION: operation(LivestockOperationType.GRAZING_ROTATION),
    WEANING: operation(LivestockOperationType.WEANING),
    CASTRATION: operation(LivestockOperationType.CASTRATION),
    DEHORNING: operation(LivestockOperationType.DEHORNING),
    IDENTIFICATION: operation(LivestockOperationType.IDENTIFICATION),
    HOOF_TRIMMING: operation(LivestockOperationType.HOOF_TRIMMING),
    CLEANING: operation(LivestockOperationType.CLEANING),
    PEST_CONTROL: operation(LivestockOperationType.PEST_CONTROL),
    ARTIFICIAL_INSEMINATION: operation(LivestockOperationType.ARTIFICIAL_INSEMINATION),
    HEAT_DETECTION: operation(LivestockOperationType.HEAT_DETECTION),
    NATURAL_BREEDING: operation(LivestockOperationType.NATURAL_BREEDING),
    PREGNANCY_DIAGNOSIS: operation(LivestockOperationType.PREGNANCY_DIAGNOSIS),
    ABORTION: operation(LivestockOperationType.ABORTION),
    CALVING: operation(LivestockOperationType.CALVING),
    WEIGHT: measurement(LivestockMeasurementType.WEIGHT),
};
/**
 * pt-BR title keyword fallback (lowercased `includes`), evaluated in order —
 * first entry with any matching keyword wins.
 */
export const TASK_TITLE_KEYWORD_TO_RECORD = [
    {
        keywords: ["vacina"],
        target: operation(LivestockOperationType.VACCINATION),
    },
    {
        keywords: ["pesag", "pesar"],
        target: measurement(LivestockMeasurementType.WEIGHT),
    },
    {
        keywords: ["diagnóstico de gestação", "toque"],
        target: operation(LivestockOperationType.PREGNANCY_DIAGNOSIS),
    },
    {
        keywords: ["insemina"],
        target: operation(LivestockOperationType.ARTIFICIAL_INSEMINATION),
    },
    {
        keywords: ["vermifug", "evermina"],
        target: operation(LivestockOperationType.TREATMENT),
    },
    {
        keywords: ["rotaç", "troca de pasto"],
        target: operation(LivestockOperationType.GRAZING_ROTATION),
    },
];
/**
 * Resolve the record target for a task: exact `operationType` match first,
 * then title keywords in declaration order. Returns null when nothing maps.
 */
export function resolveTaskRecordTarget(task) {
    if (task.operationType) {
        const exact = TASK_OPERATION_TYPE_TO_RECORD[task.operationType];
        if (exact)
            return exact;
    }
    const title = task.title?.toLowerCase();
    if (!title)
        return null;
    for (const entry of TASK_TITLE_KEYWORD_TO_RECORD) {
        if (entry.keywords.some((keyword) => title.includes(keyword))) {
            return entry.target;
        }
    }
    return null;
}
//# sourceMappingURL=TaskRecordMapping.js.map